/**
 * Knowledge base types.
 *
 * Shared types for the platform-wide knowledge base (categories, tags, articles)
 * used by municipality app (read) and super-admin app (manage).
 */

import type { LanguageSettings } from './languages';

// ==================== COLLECTION NAMES ====================

export const KB_COLLECTIONS = {
  categories: 'kbCategories',
  tags: 'kbTags',
  articles: 'kbArticles',
} as const;

// ==================== TRANSLATION TYPES ====================

export interface KBCategoryTranslation {
  name: string;
  description?: string;
}

export interface KBTagTranslation {
  name: string;
}

export interface KBArticleTranslation {
  title: string;
  content: string;
  summary?: string;
}

// ==================== SCREENSHOT ====================

/** Max length for screenshot caption (chars). */
export const SCREENSHOT_CAPTION_MAX_LENGTH = 500;

export interface KBScreenshot {
  id: string;
  url: string;
  alt: string;
  caption?: string;
  order: number;
}

// ==================== STATUS & ENUMS ====================

export type KBArticleStatus = 'draft' | 'published';

// ==================== MAIN DOCUMENT TYPES ====================

export interface KBCategory {
  id: string;
  translations: Record<string, KBCategoryTranslation>;
  languageSettings: LanguageSettings;
  order: number;
  isActive: boolean;
  showOnLanding: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface KBTag {
  id: string;
  translations: Record<string, KBTagTranslation>;
  languageSettings: LanguageSettings;
  order: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface KBArticle {
  id: string;
  categoryId: string;
  tagIds: string[];
  relatedArticleIds: string[];
  translations: Record<string, KBArticleTranslation>;
  languageSettings: LanguageSettings;
  screenshots: KBScreenshot[];
  slug: string;
  order: number;
  status: KBArticleStatus;
  authorId: string;
  authorName: string;
  createdAt: Date;
  updatedAt: Date;
  publishedAt?: Date;
}

